/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation, useQuery } from 'react-query'

import {
  deleteTrafficFilterRulesetUrl,
  getTrafficFilterRulesetsUrl,
  createTrafficFilterRulesetUrl,
  updateTrafficFilterRulesetUrl,
  createTrafficFilterRulesetAssociationUrl,
  deleteTrafficFilterRulesetAssociationUrl,
} from '@modules/cloud-api/v1/deploymentsTrafficFilter'
import type {
  BasicFailedReply,
  FilterAssociation,
  TrafficFilterRulesetRequest,
  TrafficFilterRulesetResponse,
  TrafficFilterRulesets,
} from '@modules/cloud-api/v1/types'
import { fetchAsJson } from '@modules/query/helpers'
import type { QueryHookOptions } from '@modules/query/types'
import { queryClient } from '@modules/query'

export const useListHostedTrafficFiltersQuery = (options?: QueryHookOptions) =>
  useQuery(
    ['listHostedTrafficFilters'],
    () =>
      fetchAsJson<TrafficFilterRulesets>(
        getTrafficFilterRulesetsUrl({ includeAssociations: true }),
      ),
    options,
  )

export const useCreateHostedTrafficFilterMutation = () =>
  useMutation<
    TrafficFilterRulesetResponse,
    BasicFailedReply,
    { ruleset: TrafficFilterRulesetRequest }
  >(
    ['createHostedTrafficFilter'],
    ({ ruleset }) =>
      fetchAsJson(createTrafficFilterRulesetUrl(), {
        method: 'POST',
        body: JSON.stringify(ruleset),
      }),
    { onSuccess: () => queryClient.invalidateQueries('listHostedTrafficFilters') },
  )

export const useUpdateHostedTrafficFilterMutation = () =>
  useMutation<
    TrafficFilterRulesetResponse,
    BasicFailedReply,
    { rulesetId: string; ruleset: TrafficFilterRulesetRequest }
  >(
    ['updateHostedTrafficFilter'],
    ({ rulesetId, ruleset }) =>
      fetchAsJson(updateTrafficFilterRulesetUrl({ rulesetId }), {
        method: 'PUT',
        body: JSON.stringify(ruleset),
      }),
    { onSuccess: () => queryClient.invalidateQueries('listHostedTrafficFilters') },
  )

export const useDeleteHostedTrafficFilterMutation = () =>
  useMutation<unknown, unknown, { id: string }>(
    ['deleteHostedTrafficFilter'],
    ({ id }) =>
      fetchAsJson(deleteTrafficFilterRulesetUrl({ rulesetId: id, ignoreAssociations: false }), {
        method: 'DELETE',
      }),
    { onSuccess: () => queryClient.invalidateQueries('listHostedTrafficFilters') },
  )

export const useCreateHostedTrafficFilterAssociationMutation = () =>
  useMutation<unknown, BasicFailedReply, { rulesetId: string; association: FilterAssociation }>(
    ({ rulesetId, association }) =>
      fetchAsJson<unknown>(createTrafficFilterRulesetAssociationUrl({ rulesetId }), {
        method: 'POST',
        body: JSON.stringify(association),
      }),
    { onSuccess: () => queryClient.invalidateQueries('listHostedTrafficFilters') },
  )

export const useDeleteHostedTrafficFilterAssociationMutation = () =>
  useMutation<unknown, BasicFailedReply, { rulesetId: string; deploymentId: string }>(
    ['deleteHostedTrafficFilterAssociation'],
    ({ rulesetId, deploymentId }) =>
      fetchAsJson<unknown>(
        deleteTrafficFilterRulesetAssociationUrl({
          rulesetId,
          associationType: 'deployment',
          associatedEntityId: deploymentId,
        }),
        {
          method: 'DELETE',
        },
      ),
    { onSuccess: () => queryClient.invalidateQueries('listHostedTrafficFilters') },
  )
